import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";

import { debounce } from "lodash";
import MainBody from "../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import sortCaret from "../../../components/SortCaret";
import AddEditOffice from "./AddEditOffice";
import { ResponsiveDrawerForm } from "../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import OfficeActionButton from "./OfficeActionButton";
import Confirmation from "../../../components/Confirmation";
import { useOrganization } from "../../../lib/context/OrganisationContext";
import UnauthorisedAccess from "../../../UnauthorisedAccess";

const Offices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [officesList, setOfficesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { organizationData } = useOrganization();

  const [officeId, setOfficeId] = useState(null);
  const [showAddEditOffice, setShowAddEditOffice] = useState(false);
  const [showOfficeDelete, setShowOfficeDelete] = useState(false);

  const toggleShowAddEditOffice = () => {
    if (showAddEditOffice) {
      setEdit(false);
      setOfficeId(null);
    }
    setShowAddEditOffice(!showAddEditOffice);
  };

  const toggleShowDelete = () => {
    if (showOfficeDelete) {
      setOfficeId(null);
    }
    setShowOfficeDelete(!showOfficeDelete);
  };

  const fetchOffice = useCallback(
    async (passLimit, passPage) => {
      if (organizationData?.organizationId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.OFFICE}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&organization_id=${
              organizationData?.organizationId
                ? organizationData?.organizationId
                : ""
            }`
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const officesList = data.data.data;
          let notSelectedIds = [];
          for (let i = 0; i < officesList?.length; i++) {
            notSelectedIds.push(officesList[i]?.id);
          }
          // setNotSelectedRowIds(notSelectedIds);
          setOfficesList(officesList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setOfficesList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, organizationData?.organizationId]
  );

  const officesGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: officesList[i]?.id,
        id: officesList[i]?.id,
        suburb: officesList[i]?.suburb?.name
          ? officesList[i]?.suburb?.name
          : "-",
        state: officesList[i]?.state?.name ? officesList[i]?.state?.name : "-",
        street: officesList[i]?.street ? officesList[i]?.street : "-",
        postal_code: officesList[i]?.postal_code
          ? officesList[i]?.postal_code
          : "-",
        action: (
          <>
            <OfficeActionButton
              id={officesList[i]?.id}
              setEdit={setEdit}
              setOfficeId={setOfficeId}
              toggleShowAddEditOffice={toggleShowAddEditOffice}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const offices = officesGenerator(officesList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "street",
      text: "Street",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "suburb",
      text: "Suburb",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "state",
      text: "State",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "postal_code",
      text: "Postal Code",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchOffice();
  }, [fetchOffice]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchOffice(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchOffice(limit, page);
  };

  // Single Office Delete
  const handleOfficeDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.OFFICE}/${officeId}`);
      setLoading(false);
      fetchOffice();
    } catch (error) {
      setLoading(false);
    }
  };

  return (organizationData?.organizationData?.is_subscribed || organizationData?.organizationData?.is_aml_ctf_subscribed) ? (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Add Office */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Office" : "Add Office"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setOfficeId(null);
                setShowAddEditOffice(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Office</span>
                </div>
              }
              className={""}
              open={showAddEditOffice}
              buttonClassName={""}
              setOpen={setShowAddEditOffice}
              write={true}
            >
              <AddEditOffice
                edit={edit}
                toggleShowAddEditOffice={toggleShowAddEditOffice}
                id={officeId}
                orgId={organizationData?.organizationId}
                fetchOfficeData={() => fetchOffice(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={offices}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Office"}
        open={showOfficeDelete}
        setOpen={setShowOfficeDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete office?"}
        okAction={async () => {
          handleOfficeDelete();
          toggleShowDelete();
          fetchOffice();
        }}
        cancelAction={() => {
          setOfficeId(null);
          setShowOfficeDelete(!showOfficeDelete);
        }}
      />
    </>
  ) : (
    <UnauthorisedAccess />
  );
};

export default Offices;
